import { User } from 'firebase/auth';
import { BalanceSheetStoreConfig } from '../../../pages/dashboard/balance-sheet/types';
import { Dashboard } from '../common/components/dashboards/types';
import { Filter } from '../common/components/filter/filterbar/types';
import { TimeSliderState } from '../common/components/timeslider/types';
import { Languages } from '../constants';
import {
  AllUsedFields,
  DataFieldWithDataType,
  MetricIdType,
  Segmentation,
  TimeSelection,
} from '../types';
import { ApiAlias } from './alias/types';
import { CustomSqlQueryValueBackendType } from './data/types';
import { DomainDependencies } from './domaindependencies/types';
import {
  FrontendStartup,
  FrontendStartupCurrentUser,
} from './frontendStartup/types';
import { MetricTree } from './metrics/types';
import { AssignedPermissions, Permission } from './permission/types';
import {
  AllowedDomain,
  DomainPreferences,
  DomainSettings,
  ExecutorRole,
  PerformanceCycle,
  SimulatedRole,
  UserRole,
} from './types';

export interface Serializable<T> {
  serialize: (t: T) => string;
  canSerialize: (value: unknown) => value is T;
}

class ListSerializer<T> implements Serializable<T[]> {
  constructor(private readonly serializer: Serializable<T>) {}

  serialize = (list: T[]): string => {
    return JSON.stringify(list);
  };

  canSerialize = (value: unknown): value is T[] => {
    return (
      Array.isArray(value) &&
      value.every((item) => this.serializer.canSerialize(item))
    );
  };
}

class StringSerializer implements Serializable<string> {
  serialize = (string: string): string => {
    return string;
  };

  canSerialize = (value: unknown): value is string => {
    return value !== null && typeof value === 'string';
  };
}

class DomainDependenciesSerializer implements Serializable<DomainDependencies> {
  serialize = (domainDependencies: DomainDependencies): string => {
    return JSON.stringify(domainDependencies);
  };

  canSerialize = (value: unknown): value is DomainDependencies => {
    return value !== null && typeof value === 'object' && 'domainInfo' in value;
  };
}

class FrontendStartupSerializer implements Serializable<FrontendStartup> {
  serialize = (frontendStartup: FrontendStartup): string => {
    return JSON.stringify(frontendStartup);
  };

  canSerialize = (value: unknown): value is FrontendStartup => {
    return (
      value !== null && typeof value === 'object' && 'currentUser' in value
    );
  };
}

class DomainSettingsSerializer implements Serializable<DomainSettings> {
  serialize = (settings: DomainSettings): string => {
    return JSON.stringify(settings);
  };

  canSerialize = (value: unknown): value is DomainSettings => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'domain' in value &&
      'name' in value &&
      'allowedDashboards' in value
    );
  };
}

class DomainPreferencesSerializer implements Serializable<DomainPreferences> {
  serialize = (preferences: DomainPreferences): string => {
    return JSON.stringify(preferences);
  };

  canSerialize = (value: unknown): value is DomainPreferences => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'defaultLang' in value &&
      'icrPopulationProperties' in value
    );
  };
}

class ExecutorRoleSerializer implements Serializable<ExecutorRole | null> {
  serialize = (role: ExecutorRole | null): string => {
    return JSON.stringify(role);
  };

  canSerialize = (value: unknown): value is ExecutorRole | null => {
    return (
      value === null ||
      (typeof value === 'object' &&
        value !== null &&
        'id' in value &&
        'name' in value &&
        'domain' in value)
    );
  };
}

class ExecutorRoleListSerializer extends ListSerializer<ExecutorRole | null> {
  constructor() {
    super(new ExecutorRoleSerializer());
  }
}

class SimulatedRoleSerializer implements Serializable<SimulatedRole | null> {
  serialize = (role: SimulatedRole | null): string => {
    return JSON.stringify(role);
  };

  canSerialize = (value: unknown): value is SimulatedRole | null => {
    return (
      value === null ||
      (typeof value === 'object' &&
        value !== null &&
        'id' in value &&
        'name' in value &&
        'domain' in value)
    );
  };
}

class ApiAliasSerializer implements Serializable<ApiAlias> {
  serialize = (alias: ApiAlias): string => {
    return JSON.stringify(alias);
  };

  canSerialize = (value: unknown): value is ApiAlias => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'key' in value &&
      'type' in value &&
      'translations' in value
    );
  };
}

class ApiAliasListSerializer extends ListSerializer<ApiAlias> {
  constructor() {
    super(new ApiAliasSerializer());
  }
}

class UserRoleSerializer implements Serializable<UserRole> {
  serialize = (role: UserRole): string => {
    return JSON.stringify(role);
  };

  canSerialize = (value: unknown): value is UserRole => {
    return value !== null && typeof value === 'object' && 'id' in value;
  };
}

class TimeSelectionSerializer implements Serializable<TimeSelection> {
  serialize = (timeSelection: TimeSelection): string => {
    return JSON.stringify(timeSelection);
  };

  canSerialize = (value: unknown): value is TimeSelection => {
    return value !== null && typeof value === 'object' && 'input' in value;
  };
}

class TimeSliderStateSerializer implements Serializable<TimeSliderState> {
  serialize = (timeSliderState: TimeSliderState): string => {
    return JSON.stringify(timeSliderState);
  };

  canSerialize = (value: unknown): value is TimeSliderState => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'start' in value &&
      'end' in value &&
      'range' in value &&
      'initialRange' in value &&
      'selectedGranularity' in value &&
      'granularities' in value &&
      'selectedPreset' in value &&
      'availableTimeRanges' in value &&
      'initialState' in value &&
      'granularityInfo' in value
    );
  };
}

class SegmentationSerializer implements Serializable<Segmentation> {
  serialize = (segmentation: Segmentation): string => {
    return JSON.stringify(segmentation);
  };

  canSerialize = (value: unknown): value is Segmentation => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'type' in value &&
      'dataField' in value &&
      (value.type === 'non-hierarchical' || value.type === 'hierarchical')
    );
  };
}

class SegmentationListSerializer extends ListSerializer<Segmentation> {
  constructor() {
    super(new SegmentationSerializer());
  }
}

class AssignedPermissionsSerializer<T extends Permission>
  implements Serializable<AssignedPermissions<T>>
{
  serialize = (ap: AssignedPermissions<T>): string => {
    return JSON.stringify(ap);
  };

  canSerialize = (value: unknown): value is AssignedPermissions<T> => {
    return (
      Array.isArray(value) &&
      value.every(
        (item) =>
          item !== null &&
          typeof item === 'object' &&
          'id' in item &&
          'permission' in item
      )
    );
  };
}

class DashboardSerializer implements Serializable<Dashboard> {
  serialize = (d: Dashboard): string => {
    return JSON.stringify(d);
  };

  canSerialize = (value: unknown): value is Dashboard => {
    return value !== null && typeof value === 'object' && 'id' in value;
  };
}

class DashboardListSerializer extends ListSerializer<Dashboard> {
  constructor() {
    super(new DashboardSerializer());
  }
}

class DataFieldWithDataTypeSerializer
  implements Serializable<DataFieldWithDataType>
{
  serialize = (d: DataFieldWithDataType): string => {
    return JSON.stringify(d);
  };

  canSerialize = (value: unknown): value is DataFieldWithDataType => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'dataType' in value &&
      'dataField' in value
    );
  };
}

class DataFieldWithDataTypeListSerializer extends ListSerializer<DataFieldWithDataType> {
  constructor() {
    super(new DataFieldWithDataTypeSerializer());
  }
}

class FilterSerializer implements Serializable<Filter> {
  serialize = (f: Filter): string => {
    return JSON.stringify(f);
  };

  canSerialize = (value: unknown): value is Filter => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'key' in value &&
      'label' in value &&
      'operation' in value &&
      'property' in value &&
      'values' in value &&
      'dataType' in value
    );
  };
}

class FilterListSerializer extends ListSerializer<Filter> {
  constructor() {
    super(new FilterSerializer());
  }
}

class AllowedDomainSerializer implements Serializable<AllowedDomain> {
  serialize = (d: AllowedDomain): string => {
    return JSON.stringify(d);
  };

  canSerialize = (value: unknown): value is AllowedDomain => {
    return value !== null && typeof value === 'object' && 'domain' in value;
  };
}

class AllowedDomainListSerializer extends ListSerializer<AllowedDomain> {
  constructor() {
    super(new AllowedDomainSerializer());
  }
}

class AllUsedFieldsSerializer implements Serializable<AllUsedFields[]> {
  serialize = (d: AllUsedFields[]): string => {
    return JSON.stringify(d);
  };

  canSerialize = (value: unknown): value is AllUsedFields[] => {
    return (
      Array.isArray(value) &&
      value.every(
        (item) =>
          item !== null &&
          typeof item === 'object' &&
          'dataType' in item &&
          'datafields' in item &&
          Array.isArray(item.datafields)
      )
    );
  };
}

class MetricsSerializer implements Serializable<MetricIdType[]> {
  serialize = (metrics: MetricIdType[]): string => {
    return JSON.stringify(metrics);
  };

  canSerialize = (value: unknown): value is MetricIdType[] => {
    return (
      Array.isArray(value) &&
      value.every(
        (item) =>
          item !== null &&
          typeof item === 'object' &&
          'type' in item &&
          'value' in item
      )
    );
  };
}

class MetricTreeSerializer implements Serializable<MetricTree> {
  serialize = (metricTree: MetricTree): string => {
    return JSON.stringify(metricTree);
  };

  canSerialize = (value: unknown): value is MetricTree => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'groupDetails' in value &&
      'categoryDetails' in value
    );
  };
}

class FirebaseUserSerializer implements Serializable<User> {
  serialize = (user: User): string => {
    return JSON.stringify(user);
  };

  canSerialize = (value: unknown): value is User => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'uid' in value &&
      'email' in value
    );
  };
}

class FontendStartupUserSerializer
  implements Serializable<FrontendStartupCurrentUser>
{
  serialize = (user: FrontendStartupCurrentUser): string => {
    return JSON.stringify(user);
  };

  canSerialize = (value: unknown): value is FrontendStartupCurrentUser => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'email' in value &&
      'userRole' in value &&
      'roleId' in value
    );
  };
}

class PerformanceCycleSerializer implements Serializable<PerformanceCycle> {
  serialize = (cycle: PerformanceCycle): string => {
    return JSON.stringify(cycle);
  };

  canSerialize = (value: unknown): value is PerformanceCycle => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'cycleDate' in value &&
      'cycleName' in value &&
      'cycleType' in value &&
      'cycleVizType' in value &&
      'evaluationValuesToScoreMap' in value
    );
  };
}

class PerformanceCycleListSerializer extends ListSerializer<PerformanceCycle> {
  constructor() {
    super(new PerformanceCycleSerializer());
  }
}

class CustomSqlQueryValueBackendTypeSerializer
  implements Serializable<CustomSqlQueryValueBackendType>
{
  serialize = (type: CustomSqlQueryValueBackendType): string => {
    return JSON.stringify(type);
  };

  canSerialize = (value: unknown): value is CustomSqlQueryValueBackendType => {
    return (
      value !== null &&
      typeof value === 'object' &&
      ('bigDecimal' in value ||
        'boolean' in value ||
        'reason' in value ||
        'date' in value ||
        'double' in value ||
        'int' in value ||
        'string' in value ||
        'timestamp' in value ||
        'json' in value)
    );
  };
}

class LanguageSerializer implements Serializable<Languages> {
  serialize = (language: Languages): string => {
    return JSON.stringify(language);
  };

  canSerialize = (value: unknown): value is Languages => {
    return (
      value !== null &&
      typeof value === 'string' &&
      Object.values(Languages).includes(value as Languages)
    );
  };
}

class BalanceSheetStoreConfigSerializer
  implements Serializable<BalanceSheetStoreConfig>
{
  serialize = (config: BalanceSheetStoreConfig): string => {
    return JSON.stringify(config);
  };

  canSerialize = (value: unknown): value is BalanceSheetStoreConfig => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'name' in value &&
      'initialCheckedPbsValues' in value &&
      'dashboardLinkKey' in value &&
      'balanceSheetTableService' in value &&
      'configuredMetricGroups' in value
    );
  };
}

class MetricIdTypeSerializer implements Serializable<MetricIdType> {
  serialize = (metric: MetricIdType): string => {
    return JSON.stringify(metric);
  };

  canSerialize = (value: unknown): value is MetricIdType => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'type' in value &&
      'value' in value
    );
  };
}

class MetricIdTypeListSerializer extends ListSerializer<MetricIdType> {
  constructor() {
    super(new MetricIdTypeSerializer());
  }
}

export namespace Serializer {
  export const string = new StringSerializer();
  export const domainDependencies = new DomainDependenciesSerializer();
  export const frontendStartup = new FrontendStartupSerializer();
  export const domainSettings = new DomainSettingsSerializer();
  export const domainPreferences = new DomainPreferencesSerializer();
  export const executorRole = new ExecutorRoleSerializer();
  export const executorRoleList = new ExecutorRoleListSerializer();
  export const simulatedRole = new SimulatedRoleSerializer();
  export const alias = new ApiAliasSerializer();
  export const aliasList = new ApiAliasListSerializer();
  export const userRole = new UserRoleSerializer();
  export const timeSelection = new TimeSelectionSerializer();
  export const timeSliderState = new TimeSliderStateSerializer();
  export const segmentation = new SegmentationSerializer();
  export const segmentationList = new SegmentationListSerializer();
  export const assignedPermissions =
    new AssignedPermissionsSerializer<Permission>();
  export const dashboard = new DashboardSerializer();
  export const dashboardList = new DashboardListSerializer();
  export const dataFieldWithDataType = new DataFieldWithDataTypeSerializer();
  export const dataFieldWithDataTypeList =
    new DataFieldWithDataTypeListSerializer();
  export const filters = new FilterSerializer();
  export const filterList = new FilterListSerializer();
  export const allowedDomain = new AllowedDomainSerializer();
  export const allowedDomainList = new AllowedDomainListSerializer();
  export const allUsedFields = new AllUsedFieldsSerializer();
  export const metrics = new MetricsSerializer();
  export const metricTree = new MetricTreeSerializer();
  export const firebaseUser = new FirebaseUserSerializer();
  export const frontendStartupUser = new FontendStartupUserSerializer();
  export const performanceCycle = new PerformanceCycleSerializer();
  export const performanceCycleList = new PerformanceCycleListSerializer();
  export const customSqlQueryValueBackendType =
    new CustomSqlQueryValueBackendTypeSerializer();
  export const language = new LanguageSerializer();
  export const balanceSheetStoreConfig =
    new BalanceSheetStoreConfigSerializer();
  export const metricIdType = new MetricIdTypeSerializer();
  export const metricIdTypeList = new MetricIdTypeListSerializer();
}
